import { Input } from '@affine/component';
import { FetchService } from '@affine/core/modules/cloud';
import { useService } from '@toeverything/infra';
import { debounce } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import * as styles from './styles.css';

// Convenience only: contacts autocomplete never blocks or complains about
// sending mail. The backend endpoint already absorbs every failure (missing
// grant, missing scopes, upstream error) and returns `{ contacts: [] }` — it
// never throws and never returns a non-2xx for a contacts problem. This
// component mirrors that stance: an empty or failed lookup renders nothing,
// never an error state or a spinner that implies something is broken.
const DEBOUNCE_MS = 250;
// Advisory only, to skip a pointless round-trip for a query too short to
// return anything useful. The backend enforces its own 2-character minimum
// independently and remains authoritative — this constant does not "enforce"
// anything, it just mirrors that threshold so the client doesn't bother.
const MIN_QUERY_LENGTH = 2;

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface GoogleContact {
  name: string;
  email: string;
}

/**
 * Splits a free-text recipients field into individual addresses.
 *
 * Throws (rather than silently dropping) on:
 * - any CR or LF in the raw value — a header-injection vector the backend
 *   also rejects, but failing here gives the user an inline error instead of
 *   a refused send. `trim()` does not remove interior newlines, so this is
 *   checked against the raw string before any trimming happens.
 * - any comma-separated entry that doesn't look like an email address.
 */
export function parseRecipients(raw: string): string[] {
  if (/[\r\n]/.test(raw)) {
    throw new Error('Recipients must not contain line breaks');
  }

  const addresses = raw
    .split(',')
    .map(entry => entry.trim())
    .filter(entry => entry.length > 0);

  for (const address of addresses) {
    if (!EMAIL_SHAPE.test(address)) {
      throw new Error(`"${address}" is not a valid email address`);
    }
  }

  return addresses;
}

// The user may already have complete addresses typed before the one they're
// currently working on (e.g. "a@b.com, gr|"). Autocomplete should query on
// the in-progress segment only, not the whole field.
function currentTerm(value: string): string {
  const afterLastComma = value.slice(value.lastIndexOf(',') + 1);
  return afterLastComma.trim();
}

export interface RecipientsInputProps {
  value: string;
  onChange: (value: string) => void;
}

export const RecipientsInput = ({ value, onChange }: RecipientsInputProps) => {
  const fetchService = useService(FetchService);
  const [suggestions, setSuggestions] = useState<GoogleContact[]>([]);

  // A slower earlier request resolving after a faster later one must not
  // clobber the newer suggestions ("gr" then "greg" typed quickly — if "gr"
  // resolves last, its results must be discarded). Track a sequence number
  // and only apply the response that is still the latest one issued.
  const requestIdRef = useRef(0);

  const search = useMemo(
    () =>
      debounce((term: string) => {
        const requestId = ++requestIdRef.current;
        fetchService
          .fetch(`/api/google/contacts?q=${encodeURIComponent(term)}`)
          .then(res => res.json())
          .then((data: { contacts: GoogleContact[] }) => {
            if (requestIdRef.current !== requestId) return; // superseded
            setSuggestions(data.contacts ?? []);
          })
          .catch(() => {
            // Contacts lookup is a convenience. The backend already never
            // throws for a contacts-specific problem, but a network failure
            // (offline, aborted navigation) can still reject the fetch
            // promise itself — treat it exactly like an empty result.
            if (requestIdRef.current !== requestId) return;
            setSuggestions([]);
          });
      }, DEBOUNCE_MS),
    [fetchService]
  );

  useEffect(() => {
    return () => {
      // `search.cancel()` only drops a pending *debounced* call — it can't
      // stop a fetch that has already been dispatched. Bumping the sequence
      // number here means that if such a request resolves after unmount,
      // its response handler's staleness check (`requestIdRef.current !==
      // requestId`) fails and `setSuggestions` is never called on a gone
      // component. `requestIdRef` is a plain mutable counter, not a DOM
      // node, so reading a possibly-stale `.current` here is intentional.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      ++requestIdRef.current;
      search.cancel();
    };
  }, [search]);

  useEffect(() => {
    const term = currentTerm(value);
    if (term.length < MIN_QUERY_LENGTH) {
      // Bumping the sequence number here invalidates any in-flight request
      // so a response for a since-abandoned query can't repopulate the list
      // right after we've cleared it.
      ++requestIdRef.current;
      search.cancel();
      setSuggestions([]);
      return;
    }
    search(term);
  }, [value, search]);

  // `Input` hands back the value, not the event.
  const handleInputChange = useCallback(
    (next: string) => {
      onChange(next);
    },
    [onChange]
  );

  const handlePick = useCallback(
    (email: string) => {
      const lastCommaIndex = value.lastIndexOf(',');
      const prefix =
        lastCommaIndex === -1 ? '' : value.slice(0, lastCommaIndex + 1) + ' ';
      onChange(`${prefix}${email}, `);
      setSuggestions([]);
      ++requestIdRef.current;
      search.cancel();
    },
    [value, onChange, search]
  );

  return (
    <div className={styles.recipientsField}>
      <Input
        id="send-email-recipients"
        type="text"
        size="large"
        value={value}
        onChange={handleInputChange}
        placeholder="name@example.com, second@example.com"
        aria-label="Recipients"
      />
      {suggestions.length > 0 && (
        <ul
          role="listbox"
          aria-label="Contact suggestions"
          className={styles.suggestions}
        >
          {suggestions.map(contact => (
            <li key={contact.email}>
              <button
                type="button"
                className={styles.suggestion}
                onClick={() => handlePick(contact.email)}
              >
                {contact.name} &lt;{contact.email}&gt;
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
